import React, { useEffect, useState } from 'react'
import {
  IconBold,
  IconItalic,
  IconStrikethrough,
  // IconList,
  // IconListNumbers,
  // IconBlockquote,
  IconArrowBackUp,
  IconArrowForwardUp,
  IconH1,
  IconH2,
  IconH3,
} from '@tabler/icons-react'
import { IconTypography, IconTextSize, IconPalette } from '@tabler/icons-react'
import { Color } from '@tiptap/extension-color'
import Document from '@tiptap/extension-document'
import Dropcursor from '@tiptap/extension-dropcursor'
import { FontFamily } from '@tiptap/extension-font-family'
import { FontSize } from '@tiptap/extension-font-size'
import Gapcursor from '@tiptap/extension-gapcursor'
import BulletList from '@tiptap/extension-bullet-list'
import OrderedList from '@tiptap/extension-ordered-list'
import ListItem from '@tiptap/extension-list-item'
import Blockquote from '@tiptap/extension-blockquote'
import Heading, { Level } from '@tiptap/extension-heading'
import Image from '@tiptap/extension-image'
import {TextStyle} from '@tiptap/extension-text-style'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Separator } from '@/components/ui/separator'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'

interface TextEditorProps {
  value: string
  onChange: (value: string) => void
  className?: string
  disableImage?: boolean
  disabled?: boolean
}

const ToolbarButton: React.FC<{
  onClick: () => void
  active?: boolean
  disabled?: boolean
  children: React.ReactNode
  title?: string
}> = ({ onClick, active, disabled, children, title }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        type='button'
        variant='ghost'
        size='icon'
        onClick={onClick}
        disabled={disabled}
        aria-label={title}
        className={`${active ? 'border-primary text-primary bg-muted border' : 'border border-transparent'}`}
      >
        {children}
      </Button>
    </TooltipTrigger>
    <TooltipContent>{title}</TooltipContent>
  </Tooltip>
)

export const TextEditor: React.FC<TextEditorProps> = ({
  value,
  onChange,
  className,
  disableImage,
  disabled,
}) => {
  // State to track active tools for immediate visual feedback
  const [activeTools, setActiveTools] = useState<{
    bold?: boolean
    italic?: boolean
    strike?: boolean
    heading?: number
    fontFamily?: string
    fontSize?: string
    color?: string
  }>({})

  const editor = useEditor({
    extensions: [
      StarterKit, // includes Paragraph, Heading, Blockquote, BulletList, ListItem, OrderedList, HardBreak, etc.
      TextStyle,
      FontFamily.configure({ types: ['textStyle', 'heading'] }),
      Color.configure({ types: ['textStyle', 'heading'] }),
      FontSize.configure({ types: ['textStyle', 'heading'] }),
      Heading,
      BulletList,
      OrderedList,
      ListItem,
      Blockquote,
      Dropcursor,
      Gapcursor,
      Image.configure({
        inline: true,
        allowBase64: true,
      }),
      Document,
    ],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML())
      // Update active tools state based on editor state
      setActiveTools({
        bold: editor.isActive('bold'),
        italic: editor.isActive('italic'),
        strike: editor.isActive('strike'),
        heading: editor.isActive('heading', { level: 1 }) ? 1 : 
                 editor.isActive('heading', { level: 2 }) ? 2 : 
                 editor.isActive('heading', { level: 3 }) ? 3 : undefined,
        fontFamily: editor.getAttributes('textStyle').fontFamily,
        fontSize: editor.getAttributes('textStyle').fontSize,
        color: editor.getAttributes('textStyle').color,
      })
    },
    editorProps: {
      attributes: {
        class: className || 'min-h-[360px] max-w-[786px] border rounded-md p-2',
      },
    },
  })

  const fontFamilies = [
    { label: 'Default', value: '' },
    { label: 'Arial', value: 'Arial, sans-serif' },
    { label: 'Georgia', value: 'Georgia, serif' },
    { label: 'Courier', value: 'Courier New, monospace' },
    { label: 'Times New Roman', value: 'Times New Roman, serif' },
    { label: 'Verdana', value: 'Verdana, sans-serif' },
    { label: 'Helvetica', value: 'Helvetica, sans-serif' },
    { label: 'Tahoma', value: 'Tahoma, sans-serif' },
    { label: 'Trebuchet MS', value: 'Trebuchet MS, sans-serif' },
    { label: 'Palatino', value: 'Palatino, serif' },
    { label: 'Bookman', value: 'Bookman, serif' },
    { label: 'Comic Sans MS', value: 'Comic Sans MS, cursive' },
    { label: 'Impact', value: 'Impact, sans-serif' },
    { label: 'Lucida Sans Unicode', value: 'Lucida Sans Unicode, sans-serif' },
    { label: 'Geneva', value: 'Geneva, sans-serif' },
    { label: 'Lucida Console', value: 'Lucida Console, monospace' },
  ]

  const colors = [
    { label: 'Default', value: '' },
    { label: 'Red', value: '#f00' },
    { label: 'Green', value: '#0f0' },
    { label: 'Blue', value: '#00f' },
    { label: 'Black', value: '#000' },
    { label: 'White', value: '#fff' },
    { label: 'Gray', value: '#808080' },
    { label: 'Yellow', value: '#ffff00' },
    { label: 'Orange', value: '#ffa500' },
    { label: 'Purple', value: '#800080' },
    { label: 'Pink', value: '#ffc0cb' },
    { label: 'Brown', value: '#a52a2a' },
    { label: 'Cyan', value: '#00ffff' },
    { label: 'Magenta', value: '#ff00ff' },
    { label: 'Lime', value: '#00ff00' },
    { label: 'Teal', value: '#008080' },
    { label: 'Navy', value: '#000080' },
    { label: 'Maroon', value: '#800000' },
    { label: 'Olive', value: '#808000' },
    { label: 'Silver', value: '#c0c0c0' },
    { label: 'Gold', value: '#ffd700' },
  ]

  const fontSizes = [
    { label: 'Default', value: '' },
    { label: 'Small', value: '12px' },
    { label: 'Normal', value: '16px' },
    { label: 'Large', value: '20px' },
    { label: 'Huge', value: '28px' },
  ]

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '', { emitUpdate: false })
    }
  }, [value])

  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled)
    }
  }, [disabled, editor])

  // Reset active tools when editor content changes or editor is recreated
  useEffect(() => {
    if (editor) {
      setActiveTools({
        bold: editor.isActive('bold'),
        italic: editor.isActive('italic'),
        strike: editor.isActive('strike'),
        heading: editor.isActive('heading', { level: 1 }) ? 1 : 
                 editor.isActive('heading', { level: 2 }) ? 2 : 
                 editor.isActive('heading', { level: 3 }) ? 3 : undefined,
        fontFamily: editor.getAttributes('textStyle').fontFamily,
        fontSize: editor.getAttributes('textStyle').fontSize,
        color: editor.getAttributes('textStyle').color,
      })
    }
  }, [editor])

  // Helper for heading buttons
  const headingButton = (level: number) => {
    const Icon = level === 1 ? IconH1 : level === 2 ? IconH2 : IconH3
    return (
      <ToolbarButton
        key={level}
        onClick={() => {
          editor
            ?.chain()
            .focus()
            .toggleHeading({ level: level as Level })
            .run()
          // Immediately update active state for visual feedback
          setActiveTools(prev => ({
            ...prev,
            heading: prev.heading === level ? undefined : level
          }))
        }}
        active={activeTools.heading === level || editor?.isActive('heading', { level: level as Level })}
        disabled={
          !editor
            ?.can()
            .chain()
            .focus()
            .toggleHeading({ level: level as Level })
            .run() || disabled
        }
        title={`Heading ${level}`}
      >
        <Icon size={18} />
      </ToolbarButton>
    )
  }

  return (
    <div>
      <div
        className='bg-muted mb-2 flex flex-wrap items-center gap-1 rounded-md border px-2 py-1'
        style={{ position: 'relative', zIndex: 1 }}
      >
        <ToolbarButton
          onClick={() => {
            editor?.chain().focus().toggleBold().run()
            // Immediately update active state for visual feedback
            setActiveTools(prev => ({
              ...prev,
              bold: !prev.bold
            }))
          }}
          active={activeTools.bold || editor?.isActive('bold')}
          disabled={!editor?.can().chain().focus().toggleBold().run() || disabled}
          title='Bold (Ctrl+B)'
        >
          <IconBold size={18} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => {
            editor?.chain().focus().toggleItalic().run()
            // Immediately update active state for visual feedback
            setActiveTools(prev => ({
              ...prev,
              italic: !prev.italic
            }))
          }}
          active={activeTools.italic || editor?.isActive('italic')}
          disabled={!editor?.can().chain().focus().toggleItalic().run() || disabled}
          title='Italic (Ctrl+I)'
        >
          <IconItalic size={18} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => {
            editor?.chain().focus().toggleStrike().run()
            // Immediately update active state for visual feedback
            setActiveTools(prev => ({
              ...prev,
              strike: !prev.strike
            }))
          }}
          active={activeTools.strike || editor?.isActive('strike')}
          disabled={!editor?.can().chain().focus().toggleStrike().run() || disabled}
          title='Strikethrough'
        >
          <IconStrikethrough size={18} />
        </ToolbarButton>
        <Popover>
          <PopoverTrigger asChild>
            <Button 
              type="button" 
              variant="ghost" 
              size="icon" 
              disabled={disabled} 
              aria-label="Font Family"
              className={`${activeTools.fontFamily ? 'border-primary text-primary bg-muted border' : 'border border-transparent'}`}
            >
              <IconTypography size={18} />
            </Button>
          </PopoverTrigger>
          <PopoverContent>
            {fontFamilies.map((f) => (
              <Button
                key={f.value}
                onClick={() => {
                  editor?.chain().focus().setFontFamily(f.value).run()
                  // Immediately update active state for visual feedback
                  setActiveTools(prev => ({
                    ...prev,
                    fontFamily: f.value
                  }))
                }}
                variant={
                  activeTools.fontFamily === f.value || editor?.getAttributes('textStyle').fontFamily === f.value
                    ? 'default'
                    : 'ghost'
                }
              >
                {f.label}
              </Button>
            ))}
          </PopoverContent>
        </Popover>
        <Popover>
          <PopoverTrigger asChild>
            <Button 
              type="button" 
              variant="ghost" 
              size="icon" 
              disabled={disabled} 
              aria-label="Font Size"
              className={`${activeTools.fontSize ? 'border-primary text-primary bg-muted border' : 'border border-transparent'}`}
            >
              <IconTextSize size={18} />
            </Button>
          </PopoverTrigger>
          <PopoverContent>
            {fontSizes.map((f) => (
              <Button
                key={f.value}
                onClick={() => {
                  editor?.chain().focus().setFontSize(f.value).run()
                  // Immediately update active state for visual feedback
                  setActiveTools(prev => ({
                    ...prev,
                    fontSize: f.value
                  }))
                }}
                variant={
                  activeTools.fontSize === f.value || editor?.getAttributes('textStyle').fontSize === f.value
                    ? 'default'
                    : 'ghost'
                }
              >
                {f.label}
              </Button>
            ))}
          </PopoverContent>
        </Popover>
        <Popover>
          <PopoverTrigger asChild>
            <Button 
              type="button" 
              variant="ghost" 
              size="icon" 
              disabled={disabled} 
              aria-label="Text Color"
              className={`${activeTools.color ? 'border-primary text-primary bg-muted border' : 'border border-transparent'}`}
            >
              <IconPalette size={18} />
            </Button>
          </PopoverTrigger>
          <PopoverContent>
            {colors.map((c) => (
              <Button
                key={c.value}
                onClick={() => {
                  editor?.chain().focus().setColor(c.value).run()
                  // Immediately update active state for visual feedback
                  setActiveTools(prev => ({
                    ...prev,
                    color: c.value
                  }))
                }}
                style={{ color: c.value }}
                variant={
                  activeTools.color === c.value || editor?.getAttributes('textStyle').color === c.value
                    ? 'default'
                    : 'ghost'
                }
              >
                {c.label}
              </Button>
            ))}
          </PopoverContent>
        </Popover>
        <Separator orientation='vertical' className='mx-1 h-5' />
        {headingButton(1)}
        {headingButton(2)}
        {headingButton(3)}
        <Separator orientation='vertical' className='mx-1 h-5' />
        {/* <ToolbarButton
          onClick={() => editor?.chain().focus().toggleBulletList().run()}
          active={editor?.isActive('bulletList')}
          disabled={!editor?.can().chain().focus().toggleBulletList().run()}
          title='Bullet List'
        >
          <IconList size={18} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor?.chain().focus().toggleOrderedList().run()}
          active={editor?.isActive('orderedList')}
          disabled={!editor?.can().chain().focus().toggleOrderedList().run()}
          title='Ordered List'
        >
          <IconListNumbers size={18} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor?.chain().focus().toggleBlockquote().run()}
          active={editor?.isActive('blockquote')}
          disabled={!editor?.can().chain().focus().toggleBlockquote().run()}
          title='Blockquote'
        >
          <IconBlockquote size={18} />
        </ToolbarButton> */}
        <Separator orientation='vertical' className='mx-1 h-5' />
        <ToolbarButton
          onClick={() => editor?.chain().focus().undo().run()}
          disabled={!editor?.can().chain().focus().undo().run() || disabled}
          title='Undo'
        >
          <IconArrowBackUp size={18} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor?.chain().focus().redo().run()}
          disabled={!editor?.can().chain().focus().redo().run() || disabled}
          title='Redo'
        >
          <IconArrowForwardUp size={18} />
        </ToolbarButton>
        <Separator orientation='vertical' className='mx-1 h-5' />
        {!disableImage && (
          <ToolbarButton
          onClick={() => {
            const input = document.createElement('input')
            input.type = 'file'
            input.accept = 'image/*'
            input.onchange = async (e: any) => {
              const file = e.target.files[0]
              if (file) {
                const reader = new FileReader()
                reader.onload = (event) => {
                  const base64 = event.target?.result as string
                  editor?.chain().focus().setImage({ src: base64 }).run()
                }
                reader.readAsDataURL(file)
              }
            }
            input.click()
          }}
          title='Insert Image'
          disabled={disabled}
        >
          <svg
            width='18'
            height='18'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
          >
            <rect x='3' y='3' width='18' height='18' rx='2' ry='2'></rect>
            <circle cx='8.5' cy='8.5' r='1.5'></circle>
            <polyline points='21 15 16 10 5 21'></polyline>
            </svg>
          </ToolbarButton>
        )}
      </div>
      <EditorContent editor={editor} disabled={disabled} />
    </div>
  )
}

export default TextEditor
